import type { AvailableParameter } from '../../../shared/ui/propertySelection/PropertySelectionPanel';
import { normalizeRevitParameterName } from '../../../shared/lib/revitParameterNames';
import type { PipeElement, PipeFamily } from './quickFillDataTypes';

interface GrpcAvailableParameterLike {
  name?: string;
  parameterType?: string;
  dataType?: string;
  group?: string;
  isInstance?: boolean;
}

interface BuildQuickFillAvailableParameterCatalogInput {
  pipeFamilies: PipeFamily[];
  pipeElements: PipeElement[];
  grpcAvailableParameters: GrpcAvailableParameterLike[];
}

const buildOccurrenceKey = (name: string, isInstance: boolean): string =>
  `${normalizeRevitParameterName(name).trim().toLowerCase()}::${isInstance ? 'instance' : 'type'}`;

export const buildQuickFillAvailableParameterCatalog = ({
  pipeFamilies,
  pipeElements,
  grpcAvailableParameters,
}: BuildQuickFillAvailableParameterCatalogInput): AvailableParameter[] => {
  const occurrences = new Map<string, AvailableParameter>();
  const upsert = (
    name: string | undefined,
    dataType: string | undefined,
    category: string | undefined,
    isReadOnly: boolean,
    isInstance: boolean
  ) => {
    const normalizedName = normalizeRevitParameterName(name ?? '').trim();
    if (!normalizedName) {
      return;
    }

    const key = buildOccurrenceKey(normalizedName, isInstance);
    const existing = occurrences.get(key);
    if (existing) {
      existing.isReadOnly = existing.isReadOnly === true && isReadOnly === true;
      if ((!existing.dataType || existing.dataType === 'string') && dataType) {
        existing.dataType = dataType;
      }
      if ((!existing.category || existing.category === 'General') && category) {
        existing.category = category;
      }
      return;
    }

    occurrences.set(key, {
      name: normalizedName,
      dataType: dataType || 'string',
      category: category || 'General',
      isReadOnly,
      isInstance,
    });
  };

  pipeFamilies.forEach((family) => {
    family.types?.forEach((type) => {
      type.parameterValues?.forEach((parameter) => {
        upsert(
          parameter?.parameterName,
          undefined,
          family.category,
          parameter?.isReadOnly === true,
          parameter?.isInstance === true
        );
      });
    });
  });

  pipeElements.forEach((element) => {
    element.parameterValues?.forEach((parameter) => {
      upsert(
        parameter?.parameterName,
        undefined,
        element.category,
        parameter?.isReadOnly === true,
        parameter?.isInstance !== false
      );
    });
  });

  grpcAvailableParameters.forEach((parameter) => {
    const isInstance = parameter?.isInstance === true
      || String(parameter?.parameterType || '').trim().toLowerCase() === 'instance';
    upsert(
      parameter?.name,
      parameter?.dataType,
      parameter?.group,
      false,
      isInstance
    );
  });

  return Array.from(occurrences.values()).sort((left, right) =>
    left.name.localeCompare(right.name)
      || Number(left.isInstance === true) - Number(right.isInstance === true)
  );
};

export const buildQuickFillParameterOptions = (
  parameters: ReadonlyArray<{ name?: string }>
): Array<{ value: string; label: string }> => {
  const names = new Map<string, string>();
  parameters.forEach((parameter) => {
    const name = normalizeRevitParameterName(parameter?.name ?? '').trim();
    if (name) {
      names.set(name.toLowerCase(), name);
    }
  });

  return Array.from(names.values())
    .sort((left, right) => left.localeCompare(right))
    .map((name) => ({ value: name, label: name }));
};
